import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { HeadingLevelProvider } from '@/components/common/Heading/context';
import { Section } from '@/components/layout/Section';
import { getTextColour } from '@/lib/colourUtils';

import type { TextColourTokenType } from '@/types/colours';
import type { SanityPolicyHeroType } from '@/types/sanity';

export interface PolicyHeroProps extends SanityPolicyHeroType {}

/**
When choosing a background colour, the font colour for the content on background will be picked automatically. The colour will be a complementary one to have stronger colour contrast. You can check colour accessibility here(https://webaim.org/resources/contrastchecker/). We adhere to AA guidelines.
*/

export const PolicyHero = ({ heading, backgroundColour }: PolicyHeroProps) => {
  const backgroundColourToken =
    backgroundColour?.token || '--background-primary';

  return (
    <Section backgroundColour={backgroundColourToken} className="policy-hero">
      <HeroContainer fontColour={getTextColour(backgroundColourToken)}>
        <HeadingLevelProvider level={1}>
          <CopyContainer>
            <CaptionHeadingDescription
              heading={heading}
              defaultHeadingSize="display-md"
              desktopAlignment="center"
            />
          </CopyContainer>
        </HeadingLevelProvider>
      </HeroContainer>
    </Section>
  );
};

const HeroContainer = styled.div<{
  fontColour: TextColourTokenType;
}>`
  display: flex;
  flex-direction: column;
  min-height: 240px;
  padding-top: calc(var(--section-radius) + var(--spacing-large));
  padding-right: 0;
  padding-left: 0;
  padding-bottom: calc(var(--section-radius) + var(--spacing-large));

  ${screen.md} {
    flex-direction: row;
    min-height: 400px;
  }

  color: ${(props) => `var(${props.fontColour})`};
`;

const CopyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  text-align: left;

  ${screen.md} {
    padding: 0;
    text-align: center;
  }
`;
